import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTutorials } from '@/api/hooksTutorials';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Skeleton } from '@/components/ui/skeleton';
import { LessonCatalog, LessonCatalogSkeleton } from '@/features/tutorials/LessonCatalog';
import { LessonViewer } from '@/features/tutorials/LessonViewer';
import { nextLessonOf, toCatalog, updatedLabel, type Catalog } from '@/features/tutorials/tutorialsModel';
import { useLessonProgress } from '@/features/tutorials/useLessonProgress';

/** `?lesson=<id>` opens a lesson, so a reload, a shared link or the browser's back button keep their place. */
interface TutorialsSearch {
  lesson?: string;
}

function Header({ catalog }: { catalog: Catalog | null }) {
  const updated = catalog ? updatedLabel(catalog.generatedAt) : null;
  const count = catalog?.lessons.length ?? 0;
  return (
    <header className="enter flex flex-col gap-1 border-b pb-5">
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">Tutorials</h1>
      <p className="max-w-[70ch] text-sm text-muted-foreground">Short narrated lessons that walk through Hangar screen by screen. Your progress is remembered on this device.</p>
      {catalog === null ? (
        <Skeleton className="mt-1 h-3.5 w-44" />
      ) : count > 0 ? (
        <p className="tabular mt-1 text-xs text-subtle">
          {count} {count === 1 ? 'lesson' : 'lessons'}
          {updated ? ` · ${updated}` : ''}
        </p>
      ) : null}
    </header>
  );
}

function OnTheirWay() {
  return (
    <EmptyState
      testId="tutorials-empty"
      icon="school"
      title="Tutorials are on their way"
      description="Narrated lessons on every Hangar screen will appear here as soon as they are published."
      className="rounded-lg border bg-card shadow-card"
    />
  );
}

/**
 * Tutorials (SPEC decision 20, plan P19): the narrated lessons recorded with Onboarding Studio, served by
 * `GET /v1/tutorials` (filtered by audience on the server, media signed). The catalog groups them, operator lessons
 * first under "Hangar administration"; `?lesson=` opens the player. Loading → skeleton, nothing published → one
 * designed empty state, failure → the console's error with retry.
 */
export function TutorialsPage() {
  const search = useSearch({ strict: false }) as TutorialsSearch;
  const navigate = useNavigate();
  const query = useTutorials();
  const progress = useLessonProgress();
  // The lesson the viewer opened in this page (a card, Up next, Next lesson, the countdown): it starts playing. A lesson
  // reached by a reload, a shared link or the back button waits for play (the browser would refuse sound anyway).
  const [autoPlayId, setAutoPlayId] = useState<string | null>(null);
  const catalog = useMemo(() => (query.data ? toCatalog(query.data) : null), [query.data]);
  const lesson = catalog?.lessons.find((l) => l.id === search.lesson) ?? null;

  const open = (id: string | undefined) => {
    setAutoPlayId(id ?? null);
    void navigate({ to: '/tutorials', search: { lesson: id } as TutorialsSearch, resetScroll: true });
  };
  useEffect(() => {
    if (search.lesson && catalog && !lesson) void navigate({ to: '/tutorials', search: {}, replace: true });
  }, [search.lesson, catalog, lesson, navigate]);

  if (lesson && catalog) {
    return (
      <div data-testid="tutorials-page" className="flex min-w-0 flex-col">
        <LessonViewer key={lesson.id} lesson={lesson} nextLesson={nextLessonOf(catalog.lessons, lesson.id)} progress={progress} autoPlay={autoPlayId === lesson.id} onClose={() => open(undefined)} onOpenLesson={open} />
      </div>
    );
  }
  return (
    <div data-testid="tutorials-page" className="flex min-w-0 flex-col gap-6">
      <Header catalog={catalog} />
      {query.isError && !query.data ? <ErrorState title="Couldn't load the tutorials" error={query.error} onRetry={() => void query.refetch()} className="rounded-lg border bg-card" /> : null}
      {!catalog && !query.isError ? <LessonCatalogSkeleton /> : null}
      {catalog && !catalog.available ? <OnTheirWay /> : null}
      {catalog?.available ? <LessonCatalog groups={catalog.groups} progress={progress} onOpen={(id) => open(id)} /> : null}
    </div>
  );
}
